import { useState, useCallback } from 'react';
import { getSummaryHotEventTrendBatch } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import type {
  ITrendPoint,
  ITrendBatchItem,
  TTrendMap,
} from '../components/HotEventPanel/types';

interface UseHotEventTrendOptions {
  /** 趋势时间范围，默认 14d */
  trendRange?: string;
}

/**
 * Hot Event 趋势图数据 Hook
 * 使用 /summary/hot_event/trend_batch 批量获取多个事件的迷你趋势图数据
 *
 * 调用方传入 filter（含 gameInfo + 筛选条件），
 * 再通过 fetchTrend(clusterIds) 按需拉取指定 cluster_ids 的趋势。
 */
export const useHotEventTrend = (
  filter?: any,
  options?: UseHotEventTrendOptions,
) => {
  const { trendRange = '14d' } = options || {};
  const [trendMap, setTrendMap] = useState<TTrendMap>({});
  const [timelineStartTime, setTimelineStartTime] = useState<string>('');
  const [timelineEndTime, setTimelineEndTime] = useState<string>('');
  const { loading, wrapperCancelRequest } = useCancelRequestHook();

  /**
   * 按需拉取趋势数据，支持滚动分页增量合并
   * @param clusterIds 事件 cluster_id 列表
   * @returns 按 cluster_id 分组的数据映射
   */
  const fetchTrend = useCallback(
    (clusterIds: string[]) => {
      if (!clusterIds.length || !filter) return Promise.resolve({});
      const params = {
        edition_unified_id: filter?.edition_unified_id,
        id_type: filter?.id_type || 'unified_id',
        entity_type: filter?.entity_type || 'mobile',
        cluster_ids: clusterIds,
        sentiment_type: filter?.sentiment_type || 'total',
        end_time: filter?.end_time || '',
        trend_range: trendRange,
      };

      return wrapperCancelRequest(getSummaryHotEventTrendBatch, params).then(
        (res: any) => {
          const resData = res || {};
          const list = (resData.list || []) as ITrendBatchItem[];
          const map: TTrendMap = {};
          list.forEach((item: ITrendBatchItem) => {
            map[item.cluster_id] = item.trend || [];
          });

          // 提取 API 返回的真实时间范围（首次请求时保存，后续保持一致）
          const startTime = resData.timeline_of_start_time || '';
          const endTime = resData.timeline_of_end_time || '';
          if (startTime) setTimelineStartTime(startTime);
          if (endTime) setTimelineEndTime(endTime);

          // 功能性更新：合并新数据到已有 trendMap，避免滚动加载时覆盖旧数据
          setTrendMap((prev: TTrendMap) => ({ ...prev, ...map }));
          return map;
        },
      );
    },
    [filter],
  );

  return {
    /** trendMap: { [cluster_id]: ITrendPoint[] } */
    trendMap,
    /** API 返回的趋势时间范围开始（用于构建完整时间轴） */
    timelineStartTime,
    /** API 返回的趋势时间范围结束（用于构建完整时间轴） */
    timelineEndTime,
    loading,
    fetchTrend,
    setTrendMap,
  } as const;
};
